import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import { LOGIN_URL } from '../lib/auth-client.js';
import { closeAccount } from '../lib/api.js';
import { suppressBounce } from '../lib/bounce.js';
import { Modal } from '../components/Modal.js';
import { Problem, button, input } from '../components/form.js';

/**
 * Closing your account.
 *
 * ── THE PAGE IS A ROW; THE EXPLANATION IS IN THE MODAL ───────────────
 *
 * The first version put the whole explanation on the Security screen, above the
 * button. Two paragraphs of consequences sat permanently on a page people visit
 * to change a password — so the warning was loudest for everybody who was never
 * going to press it, and by the time somebody did press it they had already
 * scrolled past the text twice and stopped reading it.
 *
 * So the screen carries one line and a button, and the explanation lives where
 * it is actually load-bearing: in front of somebody who has just said they want
 * to do this. Jon's call (2026-08-23), and it is the right way round.
 *
 * ── THE COPY IS ABOUT WHAT IS LOST, NOT WHAT IS KEPT ─────────────────
 *
 * It listed the retention of accepted agreements, and no longer does. Jon's
 * call (2026-08-23): that disclosure belongs in the Privacy Policy, which a
 * person has already acknowledged, and repeating it at the moment of deletion
 * buries the consequences that ARE specific to this button.
 *
 * What stays is everything they are about to lose, in the order it will hurt:
 * the identity, then access to every application and the data inside those
 * applications, then subscriptions and the unused time on them.
 *
 * See [[keyodo#189]] -- the Privacy Policy carrying that disclosure does not
 * exist yet, so today it is stated nowhere. That is a gap in the policy rather
 * than in this screen, and it is tracked there.
 *
 * ── WHY A TYPED CONFIRMATION AND NOT AN "ARE YOU SURE" ───────────────
 *
 * Jon's requirement, and the right one: a second button is not a second
 * decision. A dialog whose only control is "Confirm" is dismissed by the same
 * reflex that pressed the first button, with the cursor already in roughly the
 * right place. Typing a phrase cannot be done by reflex.
 */
export function CloseAccount() {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/*
        A row, not a paragraph: the thing on the left, the verb on the right.
        `h3` because it is genuinely a heading under "Danger" — the rail and a
        screen reader both need to know this section has a subject, and "Danger"
        alone names a mood rather than an action.
      */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Delete My Account</h3>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{ ...button, marginTop: 0, color: DANGER, borderColor: DANGER, flexShrink: 0 }}
        >
          Delete
        </button>
      </div>

      {/*
        Mounted always and told whether it is open, so the dialog can fade out —
        see components/Modal. `ConfirmDialog` therefore outlives a close and
        clears what was typed itself, which unmounting used to do for it.
      */}
      <ConfirmDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/**
 * The phrase somebody has to type.
 *
 * ── NOT THEIR EMAIL ADDRESS ANY MORE ─────────────────────────────────
 *
 * It was the account's address, which reads like the obvious choice and is
 * worse in practice: the address is on the screen directly above the box, so
 * the exercise degrades into copying a string from one part of the page to
 * another. A fixed phrase that STATES THE ACTION means the thing being typed is
 * the decision itself.
 *
 * Case-insensitive, per Jon. The control is meant to prove intent, not typing,
 * and rejecting `delete my account` from somebody who plainly meant it would be
 * measuring the wrong thing.
 */
const PHRASE = 'DELETE MY ACCOUNT';

export function ConfirmDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closed, setClosed] = useState(false);

  /**
   * Clear what was typed each time the dialog opens.
   *
   * Unmounting used to do this: the parent rendered `{open ? <ConfirmDialog/> :
   * null}`, so every close threw the state away. It now outlives a close so the
   * dialog can fade out, which means a reopen would otherwise show the phrase
   * from last time — already typed, already matching, with Delete live under
   * the cursor. That is precisely the reflex-press this phrase exists to stop.
   *
   * `closed` is deliberately NOT reset. Once the account is gone the dialog has
   * one true thing left to say, and reopening cannot un-delete it.
   */
  useEffect(() => {
    if (!open) return;
    setTyped('');
    setError(null);
  }, [open]);

  const matches = typed.trim().toUpperCase() === PHRASE;

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await closeAccount(typed);
      /**
       * ── STAY HERE AND SAY SO, rather than redirecting ──────────────
       *
       * Every destination this portal has is wrong for it: `/` bounces to the
       * sign-in page, so the last thing somebody sees after deleting their
       * account is a screen asking them to sign in. That reads as a failure and
       * there is no way to tell it apart from one.
       *
       * `suppressBounce` is the app's own mechanism for "the session ended
       * because they asked", used by Sign out for the same reason.
       */
      suppressBounce();
      setClosed(true);
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : 'Your account could not be closed.');
    }
  }

  if (closed) {
    return (
      /**
       * One fact, which is the only one still true for them: they are about to
       * be signed out. The previous version thanked them and listed what had
       * been removed, which is the modal restating the thing they just read and
       * agreed to. `role="status"` so it is announced rather than only seen.
       */
      <Modal open title="Your account is closed" onClose={() => window.location.assign(LOGIN_URL)}>
        <p role="status" style={{ margin: '0 0 1rem', lineHeight: 1.55 }}>
          You will now be signed out of the account portal.
        </p>
        <button
          type="button"
          onClick={() => window.location.assign(LOGIN_URL)}
          style={{ ...button, marginTop: 0 }}
        >
          Close
        </button>
      </Modal>
    );
  }

  return (
    <Modal open={open} title="Delete your account" onClose={onClose}>
      <p style={{ margin: '0 0 .75rem', lineHeight: 1.55 }}>
        This action will permanently delete the currently logged in Ridgewill identity. Your
        profile, including your name, picture, email address and passkeys are removed. All active
        sessions will be closed and you will not be able to sign back in with this account.
      </p>
      <p style={{ margin: '0 0 1.25rem', lineHeight: 1.55 }}>
        Please note that you will immediately lose access to every Ridgewill application and all
        related profile data within those applications are also permanently removed. Recurring
        subscriptions are immediately cancelled. Any remaining time with those subscriptions will
        be forfeited.{' '}
        {/*
          Bold because it is the one line here offering a way OUT. Everything
          above describes what is about to be lost; this says there is a cheaper
          option if losing the subscription is the part they mind.

          "that page" is deliberately not a link yet. Subscriptions do not live
          in the identity service, so there is nowhere to point — and a link to
          the wrong place, on the one screen that cannot be undone, is worse
          than a sentence telling somebody to go and look. Jon: link it directly
          once subscriptions land here.
        */}
        <strong>If you wish to pause or cancel subscriptions, please visit that page.</strong>
      </p>

      <label htmlFor="confirm-phrase" style={{ display: 'block', marginBottom: '.35rem' }}>
        To confirm, type <strong>{PHRASE}</strong> below
      </label>
      <input
        id="confirm-phrase"
        type="text"
        value={typed}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        onChange={(e) => setTyped(e.target.value)}
        style={input}
      />

      {/*
        NO "that does not match" hint, deliberately (Jon, 2026-08-23). A live
        mismatch message on a box somebody is halfway through typing scolds them
        for every keystroke before the last one. The label already says what to
        type and the button says whether it worked.
      */}
      {error ? <Problem>{error}</Problem> : null}

      <div style={{ display: 'flex', gap: '.75rem', marginTop: '1.25rem' }}>
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={!matches || busy}
          style={{
            ...button,
            marginTop: 0,
            /* `C.surface` — THE INVERSE OF THE GROUND, NOT WHITE (account#87).
               A fill token lightens in dark, so white on it fails: this button
               measures 2.96:1 with `#fff` against dark's `#F26C5F`, and 5.87:1
               with surface. Surface flipping to near-black in dark is the
               POINT, not a bug — a light fill wants dark text.
               org-admin's `PrimaryButton` settled this first and its brand
               equivalent measures 5.18 light / 6.30 dark the same way. */
            color: matches && !busy ? C.surface : C.muted,
            /* `C.danger` is the FILL token; `DANGER` above is `C.dangerText`
               and stays right for the outline button and Security's heading,
               which are text on the page ground. Using the text token as a
               fill is what made this pair need thinking about at all. */
            background: matches && !busy ? C.danger : 'transparent',
            borderColor: matches && !busy ? C.danger : C.inputBorder,
            cursor: matches && !busy ? 'pointer' : 'not-allowed',
          }}
        >
          {busy ? 'Deleting your account...' : 'Delete my account'}
        </button>
        <button type="button" onClick={onClose} disabled={busy} style={{ ...button, marginTop: 0 }}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

/** The same red the `Problem` message uses, so "something is wrong" and
 *  "something is dangerous" are not two different reds on one portal.
 *  A token rather than a literal, so it follows the theme (account#87). */
const DANGER = C.dangerText;
